import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Group, Image, Paper, Stack, Text, Textarea } from '@mantine/core'
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import * as Sentry from '@sentry/react'
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useProviderQuery, useUpdateProviderMutation } from '../../../api/providers/api'
import { MAX_FILE_UPLOAD_SIZE } from '../../../constants/constants'
import { useUser } from '../../../user-provider/user-context'

const MAX_BIO_LEN = 1000

export const Route = createFileRoute('/app/settings/business-settings')({
  component: BusinessSettingsComponent,
})

const businessSettingsSchema = z.object({
  bio: z.string().max(MAX_BIO_LEN, `Bio must be ${MAX_BIO_LEN} characters or less`),
})

type FormData = z.infer<typeof businessSettingsSchema>

function BusinessSettingsComponent() {
  const { user, isAdmin } = useUser()
  const providerId = user?.provider_id || ''
  const { data: provider, isLoading: providerLoading, isError: providerError } = useProviderQuery(providerId)
  const updateProviderMutation = useUpdateProviderMutation()
  const navigate = useNavigate()

  useEffect(() => {
    if (isAdmin === false) {
      navigate({ to: '/app/settings/branch-settings' })
    }
  }, [isAdmin, navigate])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(businessSettingsSchema),
    defaultValues: {
      bio: '',
    },
  })

  const [logoFile, setLogoFile] = useState<FileWithPath | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (provider) {
      reset({
        bio: provider.bio || '',
      })
      setPreviewUrl(provider.logo_image || null)
    }
  }, [provider, reset])

  const onSubmit = (data: FormData) => {
    if (providerId) {
      updateProviderMutation.mutate({
        providerId,
        data: {
          bio: data.bio,
          logoFile: logoFile || undefined,
        },
      })
    }
  }

  const handleLogoDrop = (files: FileWithPath[]) => {
    const logoFile = files[0] || null
    setLogoFile(logoFile)
    if (logoFile) {
      const url = URL.createObjectURL(logoFile)
      setPreviewUrl(url)
    }
  }

  const handleLogoRemove = () => {
    setLogoFile(null)
    setPreviewUrl(null)
  }

  return (
    <Stack maw="750px" mx="auto" mt="xl">
      <div>
        <Text size="lg" fw={500} mb="xs">
          Company logo
        </Text>

        {previewUrl ? (
          <Paper p="sm" w="100%" withBorder style={{ borderRadius: '8px' }} mb="md">
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Image src={previewUrl} alt="Logo.png" fit="contain" h={40} w={40} radius="md" />
                <Text size="sm" fw={500}>
                  {/* NOTE: we don't actually store a file name, this is just for UI purposes */}
                  Logo.png
                </Text>
              </Group>
              <Button variant="light" size="xs" onClick={handleLogoRemove} disabled={providerLoading || providerError}>
                Replace logo
              </Button>
            </Group>
          </Paper>
        ) : (
          <Dropzone
            onDrop={handleLogoDrop}
            onReject={(files) => {
              Sentry.captureException('File rejected on business settings page', {
                extra: {
                  files,
                },
              })
            }}
            maxSize={MAX_FILE_UPLOAD_SIZE}
            accept={IMAGE_MIME_TYPE}
            multiple={false}
            disabled={providerLoading || providerError}
          >
            <Group justify="center" gap="xl" mih={100} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload size={32} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={32} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={32} stroke={1.5} />
              </Dropzone.Idle>

              <div>
                <Text size="sm" inline>
                  Click to upload or drag and drop
                </Text>
                <Text size="xs" c="dimmed" inline mt={7}>
                  Supported files: PNG, JPG, JPEG or HEIC (max 50MB)
                </Text>
              </div>
            </Group>
          </Dropzone>
        )}
      </div>

      <div>
        <Text size="lg" fw={500} mb="xs">
          Bio
        </Text>
        <Controller
          name="bio"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder="Write a short bio here..."
              rows={8}
              error={errors.bio?.message}
              disabled={providerLoading || providerError}
            />
          )}
        />
        <Text size="xs" c="dimmed" mt="xs">
          {MAX_BIO_LEN} character limit
        </Text>
      </div>

      <Group justify="flex-end">
        <Button
          onClick={handleSubmit(onSubmit)}
          loading={updateProviderMutation.isPending}
          disabled={providerLoading || providerError || (!isDirty && !logoFile)}
        >
          Save changes
        </Button>
      </Group>
    </Stack>
  )
}
